import ArrowDownOutlined from '@ant-design/icons/ArrowDownOutlined';
import FolderOutlined from '@ant-design/icons/FolderOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import Button from 'antd/es/button';
import Dropdown from 'antd/es/dropdown';
import Modal from 'antd/es/modal';
import Skeleton from 'antd/es/skeleton';
import theme from 'antd/es/theme';
import Typography from 'antd/es/typography';
import uniqBy from 'lodash.uniqby';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { styled } from 'styled-components';

import { type Project } from '@mai/types';

import CreateProjectForm from '../forms/CreateProjectForm';

import { useProjectQuery, useProjectsQuery } from '@queries/projects';

const StyledButton = styled(Button)`
  margin-left: 4px;
  margin-right: 4px;
  &.collapsed {
    & > span {
      display: none;
    }
    & > span:nth-of-type(1) {
      display: inline;
    }
  }
`;

const ProjectSelect = ({ collapsed }: { collapsed: boolean }) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { token } = theme.useToken();
  const { teamId, projectId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const projectsQuery = useProjectsQuery(
    { teamIds: teamId ? [teamId] : [] },
    !!teamId,
  );
  const urlProjectQuery = useProjectQuery(projectId);

  useEffect(() => {
    if (
      !projectId &&
      !projectsQuery.isLoading &&
      projectsQuery.data?.length === 1
    ) {
      navigate(`/team/${teamId}/project/${projectsQuery.data[0].projectId}`, {
        replace: true,
      });
    }
  }, [
    projectId,
    projectsQuery.isLoading,
    projectsQuery.data,
    teamId,
    navigate,
  ]);

  const handleCreateProject = (project: Project) => {
    setIsCreateModalOpen(false);
    navigate(`/team/${teamId}/project/${project.projectId}`);
  };

  const handleOpenCreateModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDropdownOpen(false);
    setIsCreateModalOpen(true);
  };

  if (projectsQuery.isLoading || urlProjectQuery.isLoading) {
    return (
      <Skeleton.Button
        active
        style={{
          width: 'calc(100% - 10px)',
          margin: '0 5px',
          minWidth: 0,
        }}
      />
    );
  }

  const projects: Project[] = uniqBy(
    [
      ...(projectsQuery.data ?? []),
      ...(urlProjectQuery.data ? [urlProjectQuery.data] : []),
    ],
    'projectId',
  );
  const selectedProject = projects.find(
    (project) => project.projectId === projectId,
  );

  return (
    <>
      <Dropdown
        trigger={['click']}
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        menu={{
          items: [
            {
              key: 'actions',
              type: 'group',
              label: (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0',
                  }}
                >
                  <Button
                    icon={<PlusOutlined />}
                    size="small"
                    onClick={handleOpenCreateModal}
                  >
                    New Project
                  </Button>
                </div>
              ),
            },
            {
              key: 'divider',
              type: 'divider',
            },
            ...projects.map((project) => {
              let link: string;
              if (project.projectId === projectId) {
                link = pathname;
              } else {
                link = `/team/${teamId}/project/${project.projectId}`;
              }
              return {
                key: project.projectId,
                label: <Link to={link}>{project.name}</Link>,
                title: "",
              };
            }),
          ],
        }}
      >
        <StyledButton
          style={{
            borderRadius: token.borderRadiusLG,
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'space-between',
            width: 'calc(100% - 8px)',
          }}
          className={collapsed ? 'collapsed' : ''}
        >
          <FolderOutlined />
          <Typography.Text
            ellipsis
            type={selectedProject ? undefined : 'secondary'}
          >
            {`Project: ${selectedProject?.name ?? 'No Project Selected'}`}
          </Typography.Text>
          <ArrowDownOutlined />
        </StyledButton>
      </Dropdown>
      <Modal
        title="Create New Project"
        open={isCreateModalOpen}
        onCancel={() => setIsCreateModalOpen(false)}
        footer={null}
      >
        {teamId && (
          <CreateProjectForm
            teamId={teamId}
            afterSubmit={handleCreateProject}
          />
        )}
      </Modal>
    </>
  );
};

export default ProjectSelect;
